/**
 * CompanyDashboardEditKeyResultsPopup
 *
 * A Formik-driven dialog for editing a Key Result: title, description, status,
 * owners, dates, comments, and linked objectives (with weightage). Supports
 * adding/removing objective links and tracks removals for API payloads.
 *
 * Props (partial, from parent usage)
 * - isOpenAddPopup: boolean — controls dialog visibility
 * - onCancel: () => void — closes the dialog
 * - statusListObjectives: list of objective statuses
 * - keyResultId: number — KR to load/edit
 * - setLoading: (loading: boolean) => void — parent-level loading toggle
 *
 * Behavior
 * - Fetches KR details, status options, and employees on mount given keyResultId
 * - Validates dates and required fields via Yup schema
 * - Emits update payload via editKeyresult with transformed owners/objectives
 */
import React, { useState, useEffect } from "react";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputLabel,
  Tooltip,
} from "@mui/material";
import { getKeyResultById } from "../../../../../api/initiative";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import addCirculIcon from "../../../../../assets/icons/addREC.svg";
import { debounce } from "lodash";
import {
  SelectInputComponent,
  TextInputComponent,
} from "../../../../../common/components";
import {
  getAllEmployees,
  getAllStatusForKeyResults,
} from "../../../../../api/dashboard";
import MUITable from "../../../../../common/components/mui-table/mui-table";
import DatePickerComponent from "../../../../../common/components/date-picker/date-picker-compoenent";
import WarningConfirmationPopup from "../../../../../common/components/warning-confirmation-popup/warning-confirmation-popup";
import CompanyDashboardAddExistingObjectivesPopup from "../../../../../features/company-dashboard/company-dashboard-add-existing-objectives-popup/company-dashboard-add-existing-objectives-popup";
import CloseIcon from "../../../../../assets/icons/closeIcon.svg";
import deleteRedIcon from "../../../../../assets/icons/linkBreak.svg";
import "./company-dashboard-edit-key-results-popup.scss";
import { editKeyresult } from "../../../../../api/objectives";
import dayjs from "dayjs";
import { getKeyResultTitle } from "../../../../../api/title-search";

const CompanyDashboardEditKeyResultsPopup: React.FC<any> = (props: any) => {
  const {
    isOpenAddPopup,
    onCancel,
    statusListObjectives,
    keyResultId,
    setLoading,
  } = props;
  const [isOpenAddObjectivePopup, setIsOpenAddObjectivePopup] = useState(false);
  const [selectedKeyResult, setSelectedKeyResult] = useState<any>(null);
  const [statusListKeyResults, setStatusListKeyResults] = useState<any>([]);
  const [employeesList, setEmployeesList] = useState<any>([]);
  const [isDeleteWarningOpen, setIsDeleteWarningOpen] = useState(false);
  const [selectedDeleteObjective, setSelectedDeleteObjective] =
    useState<any>(null);

  const headers = [
    { key: "title", label: "Title" },
    { key: "weightage", label: "Weightage" },
    { key: "actions", label: "" },
  ];
  const [titleSuggestions, setTitleSuggestions] = useState<any[]>([]);
  const [loadingTitles, setLoadingTitles] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);

  // Clear search state when popup closes
  React.useEffect(() => {
    if (!isOpenAddPopup) {
      setTitleSuggestions([]);
      setHasSearched(false);
      setAutocompleteOpen(false);
      setLoadingTitles(false);
    }
  }, [isOpenAddPopup]);

  // Debounced function to search for initiative titles
  const searchKeyResultTitles = React.useMemo(
    () =>
      debounce(async (searchValue: string) => {
        if (!searchValue || searchValue.trim().length < 2) {
          setTitleSuggestions([]);
          setHasSearched(false);
          return;
        }

        setLoadingTitles(true);
        setHasSearched(true);
        try {
          const response = await getKeyResultTitle({ title: searchValue });
          const keyresults = response?.data?.data || [];
          setTitleSuggestions(keyresults);
        } catch {
          // If 404 or no results found, set empty array to show "No results found" message
          setTitleSuggestions([]);
        } finally {
          setLoadingTitles(false);
        }
      }, 500),
    [],
  );

  const fetchAllStatusForKeyResults = async () => {
    setLoading(true);
    try {
      const response = await getAllStatusForKeyResults();
      setStatusListKeyResults(response?.data?.data || []);

      setTimeout(() => setLoading(false), 0);
    } catch {
      setLoading(false);
    }
  };

  const fetchAllEmployees = async () => {
    setLoading(true);
    try {
      const response = await getAllEmployees({});
      setEmployeesList(response?.data?.data || []);
      setTimeout(() => setLoading(false), 0);
    } catch {
      setLoading(false);
    }
  };

  const fetchKeyResultById = async () => {
    setLoading(true);
    try {
      const response = await getKeyResultById(keyResultId);

      setSelectedKeyResult(response?.data?.data);
      setTimeout(() => setLoading(false), 0);
    } catch {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (keyResultId && !selectedKeyResult) {
      fetchKeyResultById();
      fetchAllStatusForKeyResults();
      fetchAllEmployees();
    }
  }, [keyResultId]);

  const handleCloseAddObjectivePopup = () => {
    setIsOpenAddObjectivePopup(false);
  };

  const handleOpenAddObjectivePopup = () => {
    setIsOpenAddObjectivePopup(true);
  };

  const validationSchema = Yup.object().shape({
    title: Yup.string().trim().required("Title is required"),
    owner: Yup.array().min(1, "Owner is required"),
    startDate: Yup.date()
      .nullable()
      .transform((value, originalValue) => {
        return originalValue === "" ? null : value;
      })
      .min(new Date(1900, 0, 1), "Start Date must be after 1900")
      .required("Start Date is required")
      .test(
        "start-before-end",
        "Start Date cannot be after End Date",
        function (value, context) {
          const { endDate } = context.parent;
          if (value && endDate) {
            return value <= endDate;
          }
          return true;
        },
      ),
    endDate: Yup.date()
      .nullable()
      .transform((value, originalValue) => {
        return originalValue === "" ? null : value;
      })
      .min(new Date(1900, 0, 1), "End Date must be after 1900")
      .required("End Date is required")
      .test(
        "end-after-start",
        "End Date cannot be earlier than Start Date",
        function (value, context) {
          const { startDate } = context.parent;
          if (value && startDate) {
            return value >= startDate;
          }
          return true;
        },
      ),
    // status: Yup.object().nullable().required("Status is required"),
    selectedObjectiveData: Yup.array().min(
      1,
      "At least one linked objective is required",
    ),
  });

  const getWeightageFormattedData = (
    weightage: any,
    setFieldValue: any,
    data: any,
    item: any,
  ) => {
    return (
      <div className="form-group form-group-section-four-column">
        <TextInputComponent
          type="text"
          fieldName="weightage"
          placeholder="Enter Weightage"
          value={item?.weightage ? `${item.weightage}` : ""}
          isLocked={true}
          lockedIcons={
            <i
              className="fa fa-percent"
              style={{ color: "#0000008a", fontWeight: "normal" }}
            />
          }
          onChange={(e: any) => {
            if (e?.target?.value === "" || /^\d+$/.test(e?.target?.value)) {
              setFieldValue(
                "selectedObjectiveData",
                data.map((obj: any) =>
                  obj.objectiveId === item.objectiveId
                    ? { ...obj, weightage: e?.target?.value }
                    : obj,
                ),
              );
            }
          }}
        />
      </div>
    );
  };

  const getActionsFormattedData = (
    item: any,
    setFieldValue: any,
    data: any,
    formValues: any,
  ) => {
    return (
      <button
        style={{
          background: "none",
          border: "none",
          padding: 0,
          cursor: item?.isShowDelete ? "not-allowed" : "pointer",
        }}
        onClick={() => {
          if (data?.length > 1) {
            const updatedData = data.filter(
              (obj: any) => obj.objectiveId !== item.objectiveId,
            );
            const deletedObjective = selectedKeyResult?.objectives?.find(
              (obj: any) => obj.objectiveId === item?.objectiveId,
            );
            if (deletedObjective) {
              const isAlreadyDeleted = formValues?.deletedObjectiveData?.find(
                (obj: any) => obj.objectiveId === deletedObjective.objectiveId,
              );
              if (!isAlreadyDeleted) {
                setFieldValue("deletedObjectiveData", [
                  ...(formValues?.deletedObjectiveData || []),
                  deletedObjective,
                ]);
              }
            }
            setFieldValue("selectedObjectiveData", updatedData);
          } else {
            setIsDeleteWarningOpen(true);
            setSelectedDeleteObjective(item);
          }
        }}
      >
        <img
          alt="Delete icon"
          src={deleteRedIcon}
          className="close-icon"
          style={{ cursor: item?.isShowDelete ? "not-allowed" : "pointer" }}
        />
      </button>
    );
  };

  const formattedData = (data: any, setFieldValue: any, formValues: any) => {
    if (Array.isArray(data) && data.length > 0) {
      return data.map((item: any) => {
        const statusForItem = statusListObjectives?.find(
          (status: any) => status.statusName === item?.status?.statusName,
        );

        if (!statusForItem) {
          item.status = { statusId: 3, statusName: "Not Started" };
        }

        return {
          id: item.objectiveId,
          title: item?.objectiveName || "-",
          weightage: getWeightageFormattedData(
            item?.weightage,
            setFieldValue,
            data,
            item,
          ),
          actions: getActionsFormattedData(
            item,
            setFieldValue,
            data,
            formValues,
          ),
          data: item,
        };
      });
    }
    return [];
  };

  const getInitialValues = () => {
    return {
      title: selectedKeyResult?.title || "",
      description: selectedKeyResult?.description || "",
      owner:
        selectedKeyResult?.owners?.length > 0
          ? selectedKeyResult?.owners?.map(
              (owner: any) =>
                employeesList?.find(
                  (emp: any) => emp.employeeId === owner.employeeId,
                )?.employeeFullName || "",
            )
          : [],
      ownerId: selectedKeyResult?.owners?.map((o: any) => o.employeeId) || [],
      startDate: selectedKeyResult?.startDate
        ? dayjs(selectedKeyResult.startDate)
        : null,
      endDate: selectedKeyResult?.endDate
        ? dayjs(selectedKeyResult.endDate)
        : null,
      comments: selectedKeyResult?.comments || "",
      selectedObjectiveData:
        selectedKeyResult?.objectives?.map((objectivesData: any) => ({
          objectiveId: objectivesData?.objectiveId,
          objectiveName: objectivesData?.objectiveName,
          weightage: objectivesData?.weightage,
        })) || [],
      deletedObjectiveData: [],
    };
  };

  const onSubmitHandler = (values: any, { resetForm }: any) => {
    const originalOwnerIds =
      selectedKeyResult?.owners?.map((o: any) => o.employeeId) || [];

    const newOwnerIds =
      values.owner
        ?.map(
          (name: string) =>
            employeesList?.find((emp: any) => emp.employeeFullName === name)
              ?.employeeId,
        )
        .filter(Boolean) || [];

    const addedOwners = newOwnerIds
      .filter((id: number) => !originalOwnerIds.includes(id))
      .map((id: number) => ({ ownerId: id, isRemove: 0 }));

    const removedOwners = originalOwnerIds
      .filter((id: number) => !newOwnerIds.includes(id))
      .map((id: number) => ({ ownerId: id, isRemove: 1 }));

    const ownerIdsPayload = [...addedOwners, ...removedOwners];

    const newKeyResult = {
      title: values.title,
      description: values.description,
      // status: values.status?.statusName || "",
      ownerIds: ownerIdsPayload,
      startDate: values.startDate
        ? dayjs(values.startDate).toISOString()
        : null,
      endDate: values.endDate ? dayjs(values.endDate).toISOString() : null,
      pk: selectedKeyResult?.pk,
      comments: values.comments,
      objectives: [
        ...values.selectedObjectiveData.map((obj: any) => ({
          objectiveId: obj.objectiveId,
          weightage: Number.parseFloat(obj.weightage) || 0,
        })),
        ...values.deletedObjectiveData.map((obj: any) => ({
          objectiveId: obj.objectiveId,
          weightage: Number.parseFloat(obj.weightage) || 0,
          isRemove: true,
        })),
      ],
    };

    handleUpdateKeyResult(newKeyResult, resetForm);
  };

  const handleUpdateKeyResult = async (krObj: any, resetForm: any) => {
    setLoading(true);
    try {
      const payload = {
        ownerIds: krObj.ownerIds,
        title: krObj.title,
        description: krObj.description,
        weightage: krObj.weightage,
        startDate: krObj.startDate,
        endDate: krObj.endDate,
        comments: krObj.comments,
        objectives: krObj?.objectives || [],
      };

      await editKeyresult(krObj.pk, payload);
      resetForm();
      onCancel();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectedObjectiveData = (
    data: any,
    setFieldValue: any,
    values: any,
  ) => {
    const existingObjectiveIds = values?.deletedObjectiveData?.filter(
      (obj: { objectiveId: number }) =>
        !data?.find(
          (d: { objectiveId: number }) => d.objectiveId === obj.objectiveId,
        ),
    );
    setFieldValue("deletedObjectiveData", existingObjectiveIds);
    setFieldValue("selectedObjectiveData", data);
  };

  const handleCancelDelete = () => {
    setIsDeleteWarningOpen(false);
  };
  const handleConfirmDelete = (
    setFieldValue: any,
    data: any,
    formValues: any,
  ) => {
    const updatedData = data.filter(
      (obj: any) => obj.objectiveId !== selectedDeleteObjective.objectiveId,
    );
    const deletedObjective = selectedKeyResult?.objectives?.find(
      (obj: any) => obj.objectiveId === selectedDeleteObjective?.objectiveId,
    );
    if (deletedObjective) {
      const isAlreadyDeleted = formValues?.deletedObjectiveData?.find(
        (obj: any) => obj.objectiveId === deletedObjective.objectiveId,
      );
      if (!isAlreadyDeleted) {
        setFieldValue("deletedObjectiveData", [
          ...(formValues?.deletedObjectiveData || []),
          deletedObjective,
        ]);
      }
    }
    setFieldValue("selectedObjectiveData", updatedData);
    setIsDeleteWarningOpen(false);
    setSelectedDeleteObjective(null);
  };

  const slotsPaperTitleResults = (
    props: any,
    {
      values,
      loadingTitles,
      hasSearched,
      titleSuggestions,
      setTitleSuggestions,
      setHasSearched,
      setAutocompleteOpen,
    }: any,
  ) => {
    const showNoResults =
      !loadingTitles &&
      hasSearched &&
      titleSuggestions.length === 0 &&
      values.title.trim().length >= 2;

    const showLoading = loadingTitles;

    const renderContent = () => {
      if (showLoading) {
        return (
          <div
            style={{
              padding: "8px 16px",
              color: "#666",
              fontSize: "14px",
              textAlign: "center",
            }}
          >
            Searching...
          </div>
        );
      }

      if (showNoResults) {
        return (
          <div
            style={{
              padding: "8px 16px",
              color: "#666",
              fontSize: "14px",
              textAlign: "center",
            }}
          >
            No results found.
          </div>
        );
      }

      if (titleSuggestions.length === 0) {
        return (
          <div
            style={{
              padding: "8px 16px",
              color: "#666",
              fontSize: "14px",
              textAlign: "center",
            }}
          >
            Search
          </div>
        );
      }

      return props.children;
    };

    return (
      <div
        {...props}
        style={{
          backgroundColor: "#fff",
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 8px",
            background: "#fff",
            borderBottom: "0.5px solid #eee",
          }}
        >
          <div className="" style={{ fontSize: "14px" }}>
            <InputLabel style={{ fontSize: "14px" }}>
              Existing Key Results
            </InputLabel>
          </div>
          <button
            style={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
            }}
            onClick={(e) => {
              e.stopPropagation();
              setTitleSuggestions([]);
              setHasSearched(false);
              setAutocompleteOpen(false);
            }}
          >
            <img
              alt="Close"
              src={CloseIcon}
              style={{
                cursor: "pointer",
                color: "#555",
                margin: "4px 0",
              }}
            />
          </button>
        </div>
        {renderContent()}
      </div>
    );
  };

  return (
    <Dialog
      open={isOpenAddPopup}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      onClose={(event, reason) => {
        if (reason === "backdropClick") {
          return;
        }
        onCancel();
      }}
      className={`company-dashboard-add-popup-container company-dashboard-edit-key-results-popup-container`}
    >
      <DialogTitle id="alert-dialog-title">
        <div className="header-wrapper">
          <div className="header-wrapper-text">Edit Key Result</div>
          <div className="image-wrapper">
            <button
              style={{
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
              }}
              onClick={onCancel}
            >
              <img alt="Close" src={CloseIcon} className="close-icon" />
            </button>
          </div>
        </div>
      </DialogTitle>
      <DialogContent>
        {selectedKeyResult &&
        statusListKeyResults?.length > 0 &&
        employeesList?.length > 0 ? (
          <Formik
            initialValues={getInitialValues()}
            validationSchema={validationSchema}
            validateOnChange={true}
            enableReinitialize={false}
            onSubmit={onSubmitHandler}
          >
            {({
              values,
              setFieldValue,
              handleSubmit,
              errors,
              validateField,
              touched,
              isValid,
              submitCount,
            }) => (
              <Form onSubmit={handleSubmit}>
                <div className="create-lead-capture-popup-confirmation-description-checkbox-input">
                  <div className="form-group form-text-input-component">
                    <InputLabel>Title</InputLabel>
                    <Autocomplete
                      freeSolo
                      options={titleSuggestions}
                      getOptionLabel={(option: any) =>
                        typeof option === "string" ? option : option.title || ""
                      }
                      loading={loadingTitles}
                      filterOptions={(x) => x}
                      open={autocompleteOpen}
                      onOpen={() => {
                        // Only open if there's meaningful content to show
                        if (
                          values.title.trim().length >= 2 ||
                          titleSuggestions.length > 0 ||
                          loadingTitles
                        ) {
                          setAutocompleteOpen(true);
                        }
                      }}
                      onClose={(event, reason) => {
                        // Always close on blur (clicking outside) or escape key
                        if (reason === "blur" || reason === "escape") {
                          setAutocompleteOpen(false);
                          return;
                        }

                        // For other reasons, keep open if there are results, loading, or no results after search
                        const shouldStayOpen =
                          titleSuggestions.length > 0 ||
                          loadingTitles ||
                          (hasSearched &&
                            !loadingTitles &&
                            values.title.trim().length >= 2);

                        // Only close if shouldn't stay open (not when showing no results)
                        if (!shouldStayOpen) {
                          setAutocompleteOpen(false);
                        }
                      }}
                      disableClearable
                      value={values.title}
                      inputValue={values.title}
                      onInputChange={(event, newInputValue, reason) => {
                        if (reason === "input") {
                          setFieldValue("title", newInputValue);
                          searchKeyResultTitles(newInputValue);
                          // Open dropdown when typing with enough characters
                          if (newInputValue.trim().length >= 2) {
                            setAutocompleteOpen(true);
                          } else {
                            // Close dropdown when text is cleared or too short
                            setAutocompleteOpen(false);
                            setTitleSuggestions([]);
                            setHasSearched(false);
                          }
                        }
                      }}
                      onChange={(event) => {
                        // Prevent selection - only allow typing
                        event.preventDefault();
                      }}
                      onBlur={() => {
                        // Only clear if we're not showing no results message
                        setTimeout(() => {
                          const shouldKeepOpen =
                            titleSuggestions.length > 0 ||
                            loadingTitles ||
                            (hasSearched &&
                              !loadingTitles &&
                              values.title.trim().length >= 2);

                          if (!shouldKeepOpen) {
                            setTitleSuggestions([]);
                            setHasSearched(false);
                            setAutocompleteOpen(false);
                          }
                        }, 200);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Enter Title"
                          slotProps={{
                            htmlInput: {
                              ...params.inputProps,
                              maxLength: 150,
                            },
                          }}
                        />
                      )}
                      renderOption={(props, option: any) => (
                        <Tooltip title="Selection is turned off for this field.">
                          <li
                            {...props}
                            style={{
                              cursor: "not-allowed",
                              padding: "8px 16px",
                              opacity: 0.7,
                              backgroundColor:
                                values?.title?.trim()?.toLowerCase() ===
                                option.title?.trim()?.toLowerCase()
                                  ? "#ddddfa"
                                  : "transparent !important",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor =
                                values?.title?.trim()?.toLowerCase() ===
                                option.title?.trim()?.toLowerCase()
                                  ? "#ddddfa"
                                  : "transparent !important";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor =
                                values?.title?.trim()?.toLowerCase() ===
                                option.title?.trim()?.toLowerCase()
                                  ? "#ddddfa"
                                  : "transparent !important";
                            }}
                          >
                            <div
                              style={{
                                fontSize: "14px",
                                pointerEvents: "none",
                              }}
                            >
                              {option.title}
                            </div>
                          </li>
                        </Tooltip>
                      )}
                      noOptionsText={(() => {
                        if (loadingTitles) return "Searching...";
                        if (values.title.length < 2)
                          return "Type at least 2 characters";
                        return "No results found.";
                      })()}
                      slotProps={{
                        listbox: {
                          style: {
                            maxHeight: "200px",
                          },
                        },
                      }}
                      slots={{
                        paper: (props) =>
                          slotsPaperTitleResults(props, {
                            values,
                            loadingTitles,
                            hasSearched,
                            titleSuggestions,
                            setTitleSuggestions,
                            setHasSearched,
                            setAutocompleteOpen,
                          }),
                      }}
                    />
                    <ErrorMessage
                      name="title"
                      component="div"
                      className="error-message"
                    />
                  </div>
                  <div className="form-groups-two-column">
                    <div className="form-group form-group-section-two-column">
                      <DatePickerComponent
                        fieldName="startDate"
                        label="Start Date"
                        value={values.startDate}
                        onChange={(e: any) => setFieldValue("startDate", e)}
                        minDate={dayjs("1900-01-01")}
                        maxDate={
                          values.endDate ? dayjs(values.endDate) : undefined
                        }
                      />
                    </div>

                    <div className="form-group form-group-section-two-column">
                      <DatePickerComponent
                        fieldName="endDate"
                        label="End Date"
                        value={values.endDate}
                        onChange={(e: any) => setFieldValue("endDate", e)}
                        minDate={
                          values.startDate
                            ? dayjs(values.startDate)
                            : dayjs("1900-01-01")
                        }
                      />
                    </div>
                  </div>
                  <div className="form-groups-two-column ">
                    <div className="form-group form-group-section-two-column">
                      <SelectInputComponent
                        fieldName="owner"
                        placeholder="Select Owner"
                        label="Owner"
                        multiple={true}
                        isOwnerDropdown={true}
                        options={employeesList
                          ?.filter((emp: any) =>
                            emp?.roles?.find(
                              (role: any) => role?.roleName === "ELT",
                            ) || values.owner?.includes(emp.employeeFullName),
                          )
                          ?.map((emp: any) => ({
                            value: emp.employeeFullName,
                            label: emp.employeeFullName,
                            image: emp.employeeImage || null,
                            avatar: emp.employeeImage
                              ? null
                              : emp.employeeFullName,
                          }))}
                        value={values.owner}
                        onChange={(selectedValues: string[]) => {
                          const filteredValues = selectedValues.filter(
                            (v) => v != null,
                          );
                          setFieldValue("owner", filteredValues);
                          setFieldValue(
                            "ownerId",
                            filteredValues.map(
                              (name) =>
                                employeesList?.find(
                                  (emp: any) =>// NOSONAR
                                   emp.employeeFullName === name,
                                )?.employeeId,
                            ),
                          );
                          // Trigger validation if form has been submitted before
                          if (touched.owner) {
                            setTimeout(() => {
                              validateField("owner");
                            }, 0);
                          }
                        }}
                      />
                      <ErrorMessage
                        name="owner"
                        component="div"
                        className="error-message"
                      />
                    </div>
                  </div>
                  <div className="company-dashboard-edit-key-results-object-popup-container">
                    <div className="company-dashboard-edit-key-results-object-popup-container-header">
                      <div>Linked Objectives</div>
                      <div className="company-dashboard-edit-key-results-object-popup-container-header-actions">
                        <button
                          style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            cursor: "pointer",
                          }}
                          type="button"
                          onClick={handleOpenAddObjectivePopup}
                        >
                          <img
                            src={addCirculIcon}
                            alt="key-result"
                            style={{ cursor: "pointer" }}
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="company-dashboard-edit-key-results-object-list-container">
                    <div className="error-message">
                      {typeof errors?.selectedObjectiveData === "string"
                        ? errors?.selectedObjectiveData || ""
                        : null}
                    </div>
                    <MUITable
                      columnNames={headers}
                      rowsData={[
                        ...formattedData(
                          values?.selectedObjectiveData,
                          setFieldValue,
                          values,
                        ),
                      ]}
                    />
                  </div>
                </div>
                <DialogActions>
                  <div className="action-btn-wrapper">
                    <button
                      type="button"
                      onClick={onCancel}
                      className="cancel-btn"
                    >
                      Cancel
                    </button>
                    {submitCount > 0 && !isValid ? (
                      <Tooltip
                        title="Please resolve all errors before submitting."
                        placement="top"
                      >
                        <span>
                          <button
                            type="submit"
                            autoFocus
                            className="action-btn"
                            disabled
                            style={{
                              opacity: 0.5,
                              cursor: "not-allowed",
                            }}
                          >
                            Save
                          </button>
                        </span>
                      </Tooltip>
                    ) : (
                      <button
                        type="submit"
                        autoFocus
                        className="action-btn"
                        style={{
                          opacity: 1,
                          cursor: "pointer",
                        }}
                      >
                        Save
                      </button>
                    )}
                  </div>
                </DialogActions>
                {isOpenAddObjectivePopup ? (
                  <CompanyDashboardAddExistingObjectivesPopup
                    isOpenAddPopup={isOpenAddObjectivePopup}
                    onCancel={handleCloseAddObjectivePopup}
                    setLoading={setLoading}
                    setSelectedObjectiveData={(data: any) => {
                      handleSelectedObjectiveData(data, setFieldValue, values);
                    }}
                    selectedObjectiveData={values?.selectedObjectiveData}
                  />
                ) : (
                  ""
                )}
                {isDeleteWarningOpen ? (
                  <WarningConfirmationPopup
                    isOpen={isDeleteWarningOpen}
                    onClose={handleCancelDelete}
                    onConfirm={() =>
                      handleConfirmDelete(
                        setFieldValue,
                        values?.selectedObjectiveData,
                        values,
                      )
                    }
                    title="De-link Objective"
                    description="You’re about to remove the last linked objective. Are you sure you want to delink it?"
                    confirmButtonText="Delink"
                    cancelButtonText="Cancel"
                  />
                ) : (
                  ""
                )}
              </Form>
            )}
          </Formik>
        ) : (
          <div className="no-data-found">No Data Found</div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CompanyDashboardEditKeyResultsPopup;
